import { Box, Button, Grid2 } from "@mui/material";
import { StepsEnum } from "./HorizontalLinearStepper";
import SNEvent from "./interfaces/SNEvent";
import useFetchMsal from "../../Common/UI/hooks/useFetchMsal";
import useIsAdminAndViewOnly from "../../Common/UI/hooks/useIsAdminAndViewOnly";
import useURLBuilder from "../../Common/UI/hooks/useURLBuilder";
import { useState } from "react";
import { config } from "../../authConfig";
import { LoadingIndicator } from "../../Common/UI/controls/LoadingIndicator";
import RedButton from "../../Common/UI/components/RedButton";
import InvoiceDialog from "./InvoiceDialog";
import EditedInvoice from "./interfaces/EditedInvoice";
import { CommentDialog } from "./components/CommentDialog";

export default function AccountingApprovalStep({
  handleStep,
  event,
  setEvent,
  setReportTotal,
  donationTotal,
  setEditedInvoice,
}: {
  event: SNEvent;
  handleStep: (stepNum: number) => void;
  setEvent: React.Dispatch<React.SetStateAction<SNEvent>>;
  setReportTotal: React.Dispatch<React.SetStateAction<number | null>>;
  donationTotal: number | null;
  setEditedInvoice: React.Dispatch<React.SetStateAction<EditedInvoice | null>>;
}) {
  const execute = useFetchMsal();
  const { isAdmin, isViewOnly } = useIsAdminAndViewOnly();
  const urlBuilder = useURLBuilder();
  const [isSending, setSending] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showCommentPopup, setShowCommentPopup] = useState(false);
  const handleCloseComments = () => setShowCommentPopup(false);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleGenerateReport = async function () {
    try {
      setSending(true);
      const url = urlBuilder(`${config.rootAPIUrl}${config.rootAPIList?.generateReport}`, {
        id: event.id!,
      });
      const response = await execute("GET", url);
      setReportTotal(response.total);
      setSending(false);
    } catch (error) {
      console.error(error);
      throw new Error("Calculate Donation has failed!");
    }
  };

  const handleRejection = async function () {
    try {
      setSending(true);
      const url = urlBuilder(`${config.rootAPIUrl}${config.rootAPIList?.rejectContribution}`, {
        id: event.id!,
      });

      const response = await execute("POST", url);
      setEvent(response);
      setSending(false);
      handleStep(StepsEnum.Completed);
    } catch (error) {
      console.error(error);
      throw new Error("Event rejection failed!");
    }
  };

  const handleArchive = async function (comment: string) {
    try {
      setSending(true);
      setEvent((prevEvent) => {
        prevEvent.comment = comment;
        return prevEvent;
      });
      const url = `${config.rootAPIUrl}${config.rootAPIList?.archive}`;
      const response = await execute("POST", url, event);
      setEvent(response);
      setSending(false);
      handleStep(StepsEnum.New);
    } catch (error) {
      console.error(error);
      throw new Error("Event archival failed!");
    }
  };

  return (
    <LoadingIndicator show={isSending}>
      <Grid2 container rowSpacing={2} justifyContent={"space-between"} alignItems={"center"} sx={{ paddingBottom: "20px" }}>
        <Grid2 size={12}>
          <Box>
            {" "}
            {isAdmin && !isViewOnly ? <RedButton text="Generate invoice" disabled={false} onClick={handleDialogOpen} style={{ margin: "10px" }}></RedButton> : <></>}
            {!isViewOnly && (
              <Button
                variant="contained"
                onClick={async () => {
                  await handleGenerateReport();
                }}
                style={{ margin: "10px" }}
              >
                Calculate Donation
              </Button>
            )}
            {isAdmin && !isViewOnly && (
              <Button
                variant="outlined"
                onClick={async () => {
                  await handleRejection();
                }}
                style={{ margin: "10px" }}
              >
                Reject Contribution
              </Button>
            )}
            {isAdmin && (
              <Button variant="outlined" onClick={() => setShowCommentPopup(true)} sx={{ marginLeft: 2 }}>
                Archive
              </Button>
            )}
          </Box>
        </Grid2>

        {dialogOpen && isAdmin ? (
          <InvoiceDialog
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            event={event}
            donationTotal={donationTotal}
            handleStep={handleStep}
            setEvent={setEvent}
            setEditedInvoice={setEditedInvoice}
          ></InvoiceDialog>
        ) : null}

        {showCommentPopup ? <CommentDialog handleCloseComments={handleCloseComments} handleAction={handleArchive} /> : <></>}
      </Grid2>
    </LoadingIndicator>
  );
}
